import React from "react";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { GlobalContext } from "../../../context/GlobalContext";
import { TranslateContext } from "../../../context/TranslateContext";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-script")) return resolve(true);
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function chargedAsLine(plan, settlement, lang) {
  if (!settlement || !plan?.id) return null;
  const usd = parseFloat(String(plan.price ?? "0")) || 0;
  const local = (usd * (Number(settlement.rate) || 1)).toFixed(2);
  return `${lang?.checkoutChargedAs || "Charged as"} ${settlement.symbol}${local} ${settlement.code}`;
}

const RazorpayComp = ({ plan, paying, setPaying, gwData }) => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);

  const isLoading = paying === "razorpay";

  async function handleRazorpay() {
    setPaying("razorpay");

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      alert("Failed to load Razorpay. Check your internet connection.");
      setPaying("");
      return;
    }

    const res = await hitAxios({
      path: "/api/payment/razorpay/create-order",
      admin: false,
      post: true,
      obj: { plan_id: plan.id },
    });

    if (!res.data?.success) {
      setPaying("");
      return;
    }

    const { orderId, amount, currency, keyId, plan: planMeta } = res.data;

    const options = {
      key: keyId,
      amount: amount,
      currency: currency,
      name: planMeta.title,
      description: planMeta.description,
      order_id: orderId,

      handler: async function (response) {
        const verifyRes = await hitAxios({
          path: "/api/payment/razorpay/verify-order",
          admin: false,
          post: true,
          obj: {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            plan_id: plan.id,
          },
        });

        if (verifyRes.data?.success) {
          sessionStorage.setItem(
            "verifiedRazorpayPurchase",
            JSON.stringify({
              value: Number(verifyRes.data.amount) || 0,
              currency: verifyRes.data.currency || currency || "USD",
              transaction_id:
                verifyRes.data.transaction_id || response.razorpay_payment_id,
            }),
          );
          window.location.href = `/checkout/thank-you?gateway=razorpay&payment_id=${encodeURIComponent(
            response.razorpay_payment_id,
          )}`;
        } else {
          alert(verifyRes.data?.msg || "Payment verification failed");
          setPaying("");
        }
      },

      modal: {
        ondismiss: function () {
          setPaying("");
        },
      },

      prefill: {
        name: "",
        email: "",
      },

      theme: {
        color: "#6366f1",
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", function (response) {
      console.log("Razorpay payment failed:", response.error);
      alert(response.error?.description || "Payment failed");
      setPaying("");
    });

    rzp.open();
    setPaying("");
  }

  return (
    <Stack spacing={0.5}>
      <Button
        fullWidth
        variant="outlined"
        size="large"
        disabled={!!paying}
        onClick={handleRazorpay}
        startIcon={
          isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <span style={{ fontSize: "1.1rem" }}>💰</span>
          )
        }
        sx={{
          justifyContent: "flex-start",
          borderRadius: 2,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.95rem",
          py: 1.2,
        }}
      >
        {isLoading
          ? lang?.loading || "Loading..."
          : lang?.payWith_razorpay || "Pay with Razorpay"}
      </Button>
      {gwData?.settlement ? (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          {chargedAsLine(plan, gwData.settlement, lang)}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default RazorpayComp;
